import { ContentState } from "draft-js";
import { List, Map, Set } from "immutable";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { Page } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Post } from "@domain/post/post";
import { CourseStatus } from "@domain/lms/course/courseDraft";
import { Quiz, QuizResult } from "./quiz";
import { QuizApi } from "./quizApi";
import { QuizDraft } from "./quizDraft";

export class QuizService {
	quizStore = new SimpleStore((id) => this.quizApi.get(id));
	myQuizzesStore = new SimpleMappedStorePresentation(this.quizStore, (status: CourseStatus | "ALL", page) =>
		this.quizApi.getMyQuizzes(status, page)
	);
	profileQuizzesStore = new SimpleMappedStorePresentation(this.quizStore, (profileId: string, page) =>
		this.quizApi.getProfileQuizzes(profileId, page)
	);
	organizationQuizzesStore = new SimpleMappedStorePresentation(this.quizStore, (organizationId: string, page) =>
		this.quizApi.getOrganizationQuizzes(organizationId, page)
	);
	workspaceQuizzesStore = new SimpleMappedStorePresentation(this.quizStore, (workspaceId, page) =>
		this.quizApi.getWorkspaceQuizzes(workspaceId, page)
	);

	constructor(
		private readonly quizApi: QuizApi,
		private currentProfileService: CurrentProfileService
	) {}

	async saveQuiz(draft: QuizDraft, id?: string, publisherId?: string): Promise<Quiz> {
		const course = await (id
			? this.quizApi.saveQuiz(draft, id, publisherId)
			: this.quizApi.createQuiz(draft, publisherId));
		return course;
	}

	async submit(quizId: string, answers: Map<string, Set<number>>): Promise<QuizResult> {
		const result = await this.quizApi.validate(quizId, answers);
		this.quizStore.update(quizId, (quiz) => ({
			...quiz,
			exercises: List(result.exercises),
			participants: { byMe: true, count: quiz.participants.count + 1, score: result.score },
		}));
		return result;
	}

	async deleteQuiz(quizId: string): Promise<void> {
		await this.quizApi.deleteQuiz(quizId);
		this.quizStore.remove(quizId);
	}

	async shareQuiz(quizId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.quizApi.share(quizId, message, groupId);
	}
	async shareQuizResult(quizId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.quizApi.shareResult(quizId, message, groupId);
	}

	suggest = (count: number): Promise<Page<Quiz>> => {
		return this.quizApi.getSuggestions(
			{ page: 0, size: count },
			this.currentProfileService.currentProfile.get()?.categories ?? []
		);
	};

	async fetchParticpantAnswer(quizId: string, participantId: string): Promise<Quiz> {
		return this.quizApi.getParticipantAnswer(quizId, participantId);
	}

	search = this.quizApi.search.bind(this.quizApi);
}
